import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Users2, Sparkles, ShieldCheck } from "lucide-react";

const fmt = (n) => `$${Number(n).toFixed(2)}`;

export default function Billing() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [plans, setPlans] = useState([]);
  const [clientId, setClientId] = useState(null);
  const [configured, setConfigured] = useState(false);
  const [sub, setSub] = useState(null);
  const [seat, setSeat] = useState({ used: 0, limit: 0 });
  const [daysLeft, setDaysLeft] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [{ data: p }, { data: s }] = await Promise.all([
        api.get("/billing/plans"), api.get("/billing/subscription"),
      ]);
      setPlans(p.plans); setClientId(p.paypal_client_id); setConfigured(p.configured);
      setSub(s.subscription); setSeat(s.seat_usage); setDaysLeft(s.trial_days_left);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? Access continues until the current period ends.")) return;
    setBusy(true);
    try { await api.post("/billing/cancel"); toast.success("Subscription cancelled"); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const currentPlan = sub?.plan;
  const statusLabel = sub?.status === "trialing" ? `Free trial · ${daysLeft ?? 0} day${daysLeft === 1 ? "" : "s"} left`
    : sub?.status === "active" ? "Active" : (sub?.status || "").replace("_", " ");

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <h1 className="font-heading text-2xl font-bold sm:text-3xl">Billing & Plans</h1>
        <p className="mt-1 text-sm text-muted-foreground">Choose the plan that fits your agency. Every plan starts with a 14-day free trial.</p>
      </div>

      <Card className="flex flex-wrap items-center justify-between gap-4 p-5" data-testid="billing-status-card">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary"><ShieldCheck className="h-6 w-6" /></div>
          <div>
            <p className="text-sm text-muted-foreground">Current plan</p>
            <div className="font-heading text-lg font-semibold capitalize">
              {currentPlan === "trial" ? "Free trial" : (plans.find((p) => p.id === currentPlan)?.name || currentPlan || "—")}
              <Badge variant="outline" className="ml-2 align-middle text-xs capitalize" data-testid="billing-status-badge">{statusLabel}</Badge>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-0.5 text-sm text-muted-foreground" data-testid="seat-usage">
          <span className="flex items-center gap-2"><Users2 className="h-4 w-4" /> {seat.used} / {seat.limit} seats used</span>
          {seat.used > seat.limit && (
            <span className="text-xs text-amber-500" data-testid="overage-note">+{seat.used - seat.limit} extra user{seat.used - seat.limit > 1 ? "s" : ""} × $10/mo = ${((seat.used - seat.limit) * 10).toFixed(2)}/mo</span>
          )}
        </div>
        {isAdmin && sub?.provider === "paypal" && sub?.status === "active" && (
          <Button variant="outline" onClick={cancel} disabled={busy} data-testid="cancel-subscription-button">Cancel plan</Button>
        )}
      </Card>

      {!configured && (
        <Card className="border-amber-500/40 bg-amber-500/5 p-4 text-sm text-amber-600 dark:text-amber-400" data-testid="billing-not-configured">
          Billing checkout will turn on as soon as PayPal API credentials are added. You can still explore the plans below.
        </Card>
      )}

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        {plans.map((p) => {
          const isCurrent = currentPlan === p.id && sub?.status === "active";
          const highlight = p.id === "agency";
          return (
            <Card key={p.id} data-testid={`plan-card-${p.id}`}
              className={`relative flex flex-col p-6 ${highlight ? "border-primary shadow-lg shadow-primary/10" : ""}`}>
              {highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-primary-foreground"><Sparkles className="mr-1 inline h-3 w-3" />Most popular</span>}
              <h3 className="font-heading text-xl font-bold">{p.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{p.tagline}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-heading text-4xl font-bold">{fmt(p.price)}</span>
                <span className="text-sm text-muted-foreground">/mo</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">14-day free trial · then billed monthly</p>
              <p className="text-xs text-muted-foreground">+ $10/mo per additional user</p>
              <ul className="mt-4 space-y-2 text-sm">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Up to {p.seats} user{p.seats > 1 ? "s" : ""}</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Leads, territory & area finder</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Real-time team messaging</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Cross-agency sharing</li>
              </ul>
              <div className="mt-6">
                {isCurrent ? (
                  <Button className="w-full" disabled data-testid={`plan-current-${p.id}`}>Current plan</Button>
                ) : !isAdmin ? (
                  <Button className="w-full" variant="outline" disabled>Admin manages billing</Button>
                ) : configured && clientId ? (
                  <PayPalScriptProvider options={{ clientId, vault: true, intent: "subscription" }}>
                    <PayPalButtons
                      style={{ layout: "horizontal", height: 40, tagline: false, label: "subscribe" }}
                      forceReRender={[p.id]}
                      createSubscription={async (d, actions) => {
                        const { data } = await api.post("/billing/paypal/plan", { plan: p.id });
                        return actions.subscription.create({ plan_id: data.plan_id });
                      }}
                      onApprove={async (d) => {
                        try {
                          await api.post("/billing/paypal/activate", { plan: p.id, subscription_id: d.subscriptionID });
                          toast.success(`${p.name} plan activated — your 14-day trial has started!`);
                          refresh();
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }}
                      onError={() => toast.error("PayPal checkout could not be completed. Please try again.")}
                    />
                  </PayPalScriptProvider>
                ) : (
                  <Button className="w-full" disabled data-testid={`plan-subscribe-disabled-${p.id}`}>Start 14-day trial</Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
